import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Statistic } from 'antd';
import {
  DollarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CalculatorOutlined,
} from '@ant-design/icons';
import { PaymentStatisticsResponse } from '../../model/Payment.model';
import PaymentService from '../../service/payment-service/Payment.service';
import dayjs, { Dayjs } from 'dayjs';

interface PaymentStatisticsProps {
  selectedDate: Dayjs;
  employeeId?: number;
  isAdmin: boolean;
}

const PaymentStatistics: React.FC<PaymentStatisticsProps> = ({
  selectedDate,
  employeeId,
  isAdmin,
}) => {
  const [loading, setLoading] = useState(false);
  const [statisticsData, setStatisticsData] =
    useState<PaymentStatisticsResponse>({
      totalPayments: 0,
      completedPayments: 0,
      pendingPayments: 0,
      totalAmount: 0,
      totalAmountMonth: 0,
      startingBalance: 0,
    });

  const loadStatistics = useCallback(async () => {
    if (!selectedDate || (!isAdmin && !employeeId)) return;

    try {
      setLoading(true);
      const year = selectedDate.year();
      const month = selectedDate.month() + 1;
      const day = selectedDate.date();

      const response = isAdmin
        ? await PaymentService.getPaymentStatisticsByDate(year, month, day)
        : await PaymentService.getPaymentStatisticsByDateAndEmployee(
            year,
            month,
            day,
            employeeId!
          );

      if (response.success && response.data) {
        setStatisticsData(response.data);
      } else {
        setStatisticsData({
          totalPayments: 0,
          completedPayments: 0,
          pendingPayments: 0,
          totalAmount: 0,
          totalAmountMonth: 0,
          startingBalance: 0,
        });
      }
    } catch (error) {
      console.error('Error loading payment statistics:', error);
      setStatisticsData({
        totalPayments: 0,
        completedPayments: 0,
        pendingPayments: 0,
        totalAmount: 0,
        totalAmountMonth: 0,
        startingBalance: 0,
      });
    } finally {
      setLoading(false);
    }
  }, [selectedDate, employeeId, isAdmin]);

  useEffect(() => {
    loadStatistics();
  }, [loadStatistics]);

  // Sử dụng dữ liệu từ API
  const {
    totalPayments,
    completedPayments,
    pendingPayments,
    totalAmount,
    totalAmountMonth,
    startingBalance,
  } = statisticsData;

  // Customer 2026-05-23: trang thanh toán đáo hạn hiển thị stat
  // "Tổng giá trị + Quỹ đầu ngày" — tổng giá trị thanh toán trong ngày
  // cộng với quỹ đầu ngày của nhân viên (đã được khoá ở backend tại số dư
  // cuối ngày hôm trước).
  const totalPlusStartingBalance = (totalAmount || 0) + (startingBalance || 0);

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount);
  };

  const statisticsItems = [
    {
      title: 'Tổng thanh toán',
      value: totalPayments,
      icon: <CalculatorOutlined className="text-blue-500" />,
      color: '#2563eb', // blue-600
      bgColor: 'bg-blue-50',
    },
    {
      title: 'Đã hoàn thành',
      value: completedPayments,
      icon: <CheckCircleOutlined className="text-green-500" />,
      color: '#16a34a', // green-600
      bgColor: 'bg-green-50',
    },
    {
      title: 'Chờ xử lý',
      value: pendingPayments,
      icon: <ClockCircleOutlined className="text-orange-500" />,
      color: '#ea580c', // orange-600
      bgColor: 'bg-orange-50',
    },
    {
      title: 'Tổng giá trị',
      value: formatCurrency(totalAmount),
      icon: <DollarOutlined className="text-purple-500" />,
      color: '#9333ea', // purple-600
      bgColor: 'bg-purple-50',
    },
    {
      title: 'Tổng giá trị (tháng)',
      value: formatCurrency(totalAmountMonth),
      icon: <DollarOutlined className="text-red-500" />,
      color: '#dc2626', // red-600
      bgColor: 'bg-red-50',
    },
    {
      title: 'Tổng giá trị + Quỹ đầu ngày',
      value: formatCurrency(totalPlusStartingBalance),
      icon: <DollarOutlined className="text-teal-500" />,
      color: '#0d9488', // teal-600
      bgColor: 'bg-teal-50',
    },
  ];

  return (
    <div className="flex flex-wrap gap-4">
      {statisticsItems.map((stat, index) => (
        <div key={index} className="flex-1 min-w-[240px]">
          <Card
            loading={loading}
            className={`${stat.bgColor} border-0 shadow-sm hover:shadow-md transition-shadow duration-300 h-full`}
          >
            <Statistic
              title={
                <span className="text-gray-600 font-medium">{stat.title}</span>
              }
              value={stat.value}
              valueStyle={{
                color: stat.color,
                fontSize: '24px',
                fontWeight: '600',
              }}
              prefix={<div className="text-2xl mb-1">{stat.icon}</div>}
            />
          </Card>
        </div>
      ))}
    </div>
  );
};

export default PaymentStatistics;
